import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  TbAntennaBars1,
  TbAntennaBars2,
  TbAntennaBars3,
  TbAntennaBars4,
  TbAntennaBars5,
} from 'react-icons/tb'
import type { PriorityComboBoxProps, PriorityOption } from '../type'
import { VariantComboBox } from '@/components/app/combo-box/VariantComboBox'
import { cn } from '@/lib/utils'

// Picker order (design 31b): "No priority" first, then most to least urgent. The
// shortcut is the row position, so 0 clears and 1 is Urgent.
const priorityOptions: Array<PriorityOption & { labelKey: string }> = [
  { value: 0, label: 'No priority', labelKey: 'picker.priority.none' },
  { value: 4, label: 'Urgent', labelKey: 'tasks:priority.urgent' },
  { value: 3, label: 'High', labelKey: 'tasks:priority.high' },
  { value: 2, label: 'Medium', labelKey: 'tasks:priority.medium' },
  { value: 1, label: 'Low', labelKey: 'tasks:priority.low' },
]

const PRIORITY_ICONS: [{ Icon: typeof TbAntennaBars1; color: string }, ...Array<{ Icon: typeof TbAntennaBars1; color: string }>] = [
  { Icon: TbAntennaBars1, color: 'text-muted-foreground' },
  { Icon: TbAntennaBars2, color: 'text-blue-500' },
  { Icon: TbAntennaBars3, color: 'text-yellow-500' },
  { Icon: TbAntennaBars4, color: 'text-orange-500' },
  { Icon: TbAntennaBars5, color: 'text-red-500' },
]

/** Static priority bars for a row's leading slot (design 23); nothing for "no priority". */
export function PriorityGlyph({ priority, className }: { priority: number; className?: string }) {
  if (priority <= 0) return null
  const entry = PRIORITY_ICONS[priority] ?? PRIORITY_ICONS[0]
  const IconComponent = entry.Icon
  return <IconComponent aria-hidden className={cn('size-3.5', entry.color, className)} />
}

const getPriorityIcon = (priority: number, colorClass?: string) => {
  const entry = PRIORITY_ICONS[priority] ?? PRIORITY_ICONS[0]
  const IconComponent = entry.Icon
  return (
    <IconComponent
      className={cn(
        'h-4 w-4 transition-all duration-200 hover:brightness-110 hover:cursor-pointer',
        colorClass ?? entry.color,
      )}
    />
  )
}

export function PriorityComboBox({
  selectedPriority,
  onPriorityChange,
  placeholderText,
  searchText,
  buttonClassName,
  isDisabled = false,
  variant = 'default',
  fontSize = 'xs',
  popoverOpen,
  onPopoverOpenChange,
  triggerProps,
  triggerContent,
}: PriorityComboBoxProps) {
  const { t } = useTranslation(['common', 'tasks'])
  const mergedTriggerProps = {
    title: triggerProps?.title ?? `${t('picker.priority.title')} (P)`,
    'data-testid': 'priority-select',
    ...triggerProps,
  }

  const options = useMemo(
    () =>
      priorityOptions.map((p, index) => ({
        value: p.value.toString(),
        label: t(p.labelKey, { defaultValue: p.label }),
        icon: getPriorityIcon(p.value),
        shortcut: String(index),
      })),
    [t],
  )

  return (
    <VariantComboBox
      options={options}
      selectedValue={selectedPriority === null ? undefined : String(selectedPriority ?? 0)}
      onChange={(value) => onPriorityChange(parseInt(value, 10))}
      variant={variant}
      placeholderText={placeholderText ?? t('picker.priority.placeholder')}
      searchText={searchText ?? t('picker.priority.search')}
      buttonClassName={buttonClassName}
      isDisabled={isDisabled}
      fontSize={fontSize}
      popoverOpen={popoverOpen}
      onPopoverOpenChange={onPopoverOpenChange}
      triggerProps={mergedTriggerProps}
      fallbackIcon={getPriorityIcon(0)}
      renderTriggerIcon={
        triggerContent !== undefined ? () => triggerContent : undefined
      }
    />
  )
}
